import { Flex, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { useState } from 'react';
import { z } from 'zod';
import { useRegisterMutation } from '../../api/register';
import { SignupTerms } from '../SignupTerms/SignupTerms';
import { ReactComponent as ToastIcon } from '@/assets/svg/ToastIcon.svg';
import { Button, DisclaimerCard } from '@/components/Elements';
import { InputField, PasswordField, SelectField } from '@/components/Form';
import { CheckboxField } from '@/components/Form/CheckboxField';
import { ASSETS_BUCKET_URL } from '@/config';
import { Gender } from '@/enums/account';
import { SignupProps } from '@/types/auth';
import storage from '@/utils/storage';

type SignupValues = {
  name: string;
  email: string;
  mobile_no: string;
  password: string;
  gender: string;
  confirm_password: string;
};

type SignupFormProps = {
  onSuccess: (SignUpResponseData: SignupProps & { id: number }) => void;
  userData?: SignupProps & { id: number };
};

export const convertToRegex = (emailPattern: any) => {
  const regex = new RegExp(emailPattern);
  // default email regex is used if the email pattern is not provided
  const defaultRegex = new RegExp(
    "^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:.[a-zA-Z0-9-]+)*$"
  );
  return regex || defaultRegex;
};

export const SignupForm = ({ onSuccess, userData }: SignupFormProps) => {
  const config: any = storage.getItem('config');
  const [termsOfService, setTermsOfService] = useState(false);

  const schema = z
    .object({
      name: z.string().nonempty({ message: 'Please provide your name' }),
      email: z
        .string()
        .email({ message: 'Please provide a valid email ID' })
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        .regex(convertToRegex(config?.email_pattern), {
          message: 'Please provide a valid email ID',
        }),
      mobile_no: z
        .string()
        // .regex(/^(0|91)?[6-9][0-9]{9}$/, { message: 'Mobile number is invalid' })
        .min(10, { message: 'Please provide a valid 10-digit mobile number' })
        .max(10, { message: 'Please provide a valid 10-digit mobile numbe' }),
      gender: z.string().min(1, { message: 'Please provide your gender' }),
      password: z.string().min(8, { message: 'The password must be at least 8 characters' }),
      confirm_password: z
        .string()
        .min(8, { message: 'Password should have at least 8 characters' }),
    })
    .refine((data) => data.confirm_password === data.password, {
      message: 'Must match your new password',
      path: ['confirm_password'],
    });

  const form = useForm<SignupValues>({
    validate: zodResolver(schema),
    initialValues: userData ?? {
      name: '',
      email: '',
      mobile_no: '',
      gender: '',
      password: '',
      confirm_password: '',
    },
    validateInputOnBlur: true,
  });
  const { mutateAsync: signUpQuery, isLoading } = useRegisterMutation();

  const onSubmit = async (values: SignupValues) => {
    if (!termsOfService && config?.show_terms_and_privacy_policy_on_signup) {
      return;
    }
    const company = storage.getItem('company');
    const location = storage.getItem('currentLocation');
    if (!company && !location) {
      // setError('Company or Location not found');
      showNotification({
        title: '',
        message: 'Company or Location not found',
        icon: <ToastIcon />,
      });
      return false;
    }
    const payload = {
      ...values,
      company_id: company.id,
      employee_id: values.mobile_no,
      location_id: location.id,
      show_simpl: true,
    };
    try {
      const response = await signUpQuery(payload);
      if (response) {
        // setError(null);
      }
      onSuccess({
        ...payload,
        id: response.data.id,
      });
      // setError(null);
    } catch (error: any) {}
  };

  return (
    <div
      style={{
        paddingBottom: '2rem',
      }}
    >
      <form
        onSubmit={form.onSubmit((values) => {
          void onSubmit(values);
        })}
      >
        <Text fw={600} component="p" my="md">
          To sign up, enter details
        </Text>
        <InputField
          type="text"
          label="Name"
          register={{
            ...form.getInputProps('name'),
          }}
          onBlur={() => {
            form.validate();
          }}
          mt="sm"
          mb="sm"
        />
        <InputField
          type="email"
          label="Email ID"
          register={{ ...form.getInputProps('email') }}
          mt="sm"
          mb="sm"
        />
        <InputField
          type="text"
          label="Phone No."
          register={{ ...form.getInputProps('mobile_no') }}
          mb="sm"
          mt="sm"
          maxLength={10}
        />
        <SelectField
          label="Gender"
          data={[
            { value: Gender.MALE, label: 'Male' },
            { value: Gender.FEMALE, label: 'Female' },
            { value: Gender.OTHERS, label: 'Non Binary' },
          ]}
          register={{ ...form.getInputProps('gender') }}
          mb="xl"
        />
        <Text fw={600} component="p" className="no-margin">
          Create a password
        </Text>
        <Text span c="gray">
          *Minimum 8 characters
        </Text>
        <PasswordField label="New Password" register={{ ...form.getInputProps('password') }} />
        <PasswordField
          label="Confirm Password"
          register={{ ...form.getInputProps('confirm_password') }}
        />

        {config?.show_terms_and_privacy_policy_on_signup && config?.signup_terms ? (
          <Flex justify="flex-start" align="center" direction="row" mt="xl" mb="xl">
            <CheckboxField
              mr="xs"
              value={Number(termsOfService)}
              checked={termsOfService}
              onChange={(e) => {
                setTermsOfService(e.target.checked);
              }}
            />{' '}
            <SignupTerms />
          </Flex>
        ) : null}

        {config?.signup_disclaimer?.disclaimer_title &&
          config?.signup_disclaimer.disclaimer_description && (
            <DisclaimerCard
              title={config?.signup_disclaimer.disclaimer_title}
              description={config?.signup_disclaimer.disclaimer_description}
              imgUrl={`${ASSETS_BUCKET_URL}/svg/Alert.svg`}
            />
          )}
        <div className="auth-buttons-spacing">
          <Button
            loading={isLoading}
            type="submit"
            fullWidth
            className={
              !form.isValid() ||
              (config?.show_terms_and_privacy_policy_on_signup && !termsOfService)
                ? 'disabled-button'
                : ''
            }
          >
            Sign Up
          </Button>
        </div>
      </form>
    </div>
  );
};
